import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { listDocuments, compareDocuments } from '../services/api';
import toast from 'react-hot-toast';

const IMPACT_COLOR: Record<string, string> = {
  better: 'text-green-700 bg-green-50 border-green-200',
  neutral: 'text-gray-700 bg-gray-50 border-gray-200',
  worse: 'text-red-700 bg-red-50 border-red-200',
};

const DELTA_COLOR: Record<string, string> = {
  safer: 'text-green-700 bg-green-100',
  similar: 'text-gray-700 bg-gray-100',
  riskier: 'text-red-700 bg-red-100',
};

export default function ComparisonPage() {
  const { currentUser } = useAuth();
  const [docs, setDocs] = useState<any[]>([]);
  const [docIdA, setDocIdA] = useState('');
  const [docIdB, setDocIdB] = useState('');
  const [comparison, setComparison] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const loadDocs = useCallback(async () => {
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      const res = await listDocuments(token);
      setDocs(res.documents || []);
    } catch { /* ok */ }
  }, [currentUser]);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  const handleCompare = async () => {
    if (!docIdA || !docIdB || !currentUser) return;
    if (docIdA === docIdB) { toast.error('Select two different documents'); return; }
    setLoading(true); setComparison(null);
    try {
      const token = await currentUser.getIdToken();
      const res = await compareDocuments(token, docIdA, docIdB);
      setComparison(res.comparison);
      toast.success('Comparison complete!');
    } catch (e: any) { toast.error(e.message || 'Comparison failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-5xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Document Comparison</h1>
        <p className="text-gray-500 text-sm mt-1">Compare two contracts to identify changes, additions, and risk differences.</p>
      </div>

      {/* Document selectors */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Document A (Original)</label>
            <select value={docIdA} onChange={(e) => { setDocIdA(e.target.value); setComparison(null); }}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">— Select document A —</option>
              {docs.map((d) => <option key={d.doc_id} value={d.doc_id}>{d.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Document B (Revised)</label>
            <select value={docIdB} onChange={(e) => { setDocIdB(e.target.value); setComparison(null); }}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">— Select document B —</option>
              {docs.map((d) => <option key={d.doc_id} value={d.doc_id}>{d.label}</option>)}
            </select>
          </div>
        </div>
        <button onClick={handleCompare} disabled={!docIdA || !docIdB || loading}
          className="px-6 py-2.5 bg-indigo-700 text-white rounded-lg text-sm font-semibold hover:bg-indigo-600 disabled:opacity-50 transition-colors">
          {loading ? 'Comparing...' : '🔄 Compare Documents'}
        </button>
      </div>

      {/* Comparison Results */}
      {comparison && (
        <div className="space-y-4">
          {/* Summary banner */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-gray-800">Comparison Summary</h2>
              <span className={`text-xs font-bold px-3 py-1 rounded-full ${DELTA_COLOR[comparison.risk_delta] || 'bg-gray-100'}`}>
                Document B is {comparison.risk_delta}
              </span>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">{comparison.summary}</p>
          </div>

          {/* Columns: Added / Removed / Modified */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Added */}
            <div className="bg-green-50 border border-green-200 rounded-2xl p-5">
              <h3 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                <span className="text-lg">+</span> Added Clauses
                <span className="text-xs text-green-600 ml-auto">{comparison.added_clauses?.length || 0}</span>
              </h3>
              {comparison.added_clauses?.length ? (
                <div className="space-y-2">
                  {comparison.added_clauses.map((c: any, i: number) => (
                    <div key={i} className="bg-white/70 rounded-lg p-3">
                      <p className="text-sm font-medium text-green-900">{c.clause_name}</p>
                      <p className="text-xs text-green-700 mt-1">{c.explanation}</p>
                    </div>
                  ))}
                </div>
              ) : <p className="text-xs text-green-600">No new clauses added</p>}
            </div>

            {/* Removed */}
            <div className="bg-red-50 border border-red-200 rounded-2xl p-5">
              <h3 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
                <span className="text-lg">−</span> Removed Clauses
                <span className="text-xs text-red-600 ml-auto">{comparison.removed_clauses?.length || 0}</span>
              </h3>
              {comparison.removed_clauses?.length ? (
                <div className="space-y-2">
                  {comparison.removed_clauses.map((c: any, i: number) => (
                    <div key={i} className="bg-white/70 rounded-lg p-3">
                      <p className="text-sm font-medium text-red-900">{c.clause_name}</p>
                      <p className="text-xs text-red-700 mt-1">{c.explanation}</p>
                    </div>
                  ))}
                </div>
              ) : <p className="text-xs text-red-600">No clauses removed</p>}
            </div>

            {/* Modified */}
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
              <h3 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                <span className="text-lg">~</span> Modified Clauses
                <span className="text-xs text-amber-600 ml-auto">{comparison.modified_clauses?.length || 0}</span>
              </h3>
              {comparison.modified_clauses?.length ? (
                <div className="space-y-2">
                  {comparison.modified_clauses.map((c: any, i: number) => (
                    <div key={i} className={`rounded-lg p-3 border ${IMPACT_COLOR[c.risk_impact] || IMPACT_COLOR.neutral}`}>
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm font-medium">{c.clause_name}</p>
                        <span className="text-xs font-medium capitalize">{c.risk_impact}</span>
                      </div>
                      <p className="text-xs mt-1">{c.change}</p>
                    </div>
                  ))}
                </div>
              ) : <p className="text-xs text-amber-600">No clauses modified</p>}
            </div>
          </div>

          {/* Recommendations */}
          {comparison.recommendations?.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5">
              <h2 className="font-semibold text-blue-800 mb-2">💡 Recommendations</h2>
              <ul className="space-y-1">
                {comparison.recommendations.map((r: string, i: number) => (
                  <li key={i} className="text-sm text-blue-700 flex gap-2"><span>•</span>{r}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {!comparison && !loading && docIdA && docIdB && (
        <div className="bg-white rounded-2xl border border-dashed border-gray-300 p-12 text-center">
          <div className="text-4xl mb-3">🔄</div>
          <p className="text-sm text-gray-500">Click "Compare Documents" to see the differences</p>
        </div>
      )}
    </div>
  );
}
